import { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FONT, COLOR } from "../lib/tokens";
import { useTheme as useMCMTheme } from "../lib/theme";
import {
  monthGrid,
  weekDayLetters,
  fmtMonthHeader,
  isSameDay,
  startOfDay,
  dayKey,
} from "../lib/calendarUtils";
import { getMeasurementSystem } from "../lib/unitPrefs";

// CalendarMonth — 6×7 month grid with per-day emoji indicators.
//
// Each cell shows up to 3 meal emojis with a status hint:
//   ✓  cooked (past cooks AND scheduled rows marked status=cooked)
//   •  planned (default scheduled meal)
//   🙋 requested (cook_id is null, awaiting a family member to claim)
//
// Cell height is 80px so 2 emojis fit comfortably under the date number;
// a 3rd emoji uses a tighter row. More than 3 meals on a day collapses
// the overflow into a "+N" pill.

const CELL_HEIGHT = 84;
const MAX_EMOJI_ROWS = 3;

export default function CalendarMonth({
  cursorDate,
  meals = [],
  pastCooks = [],
  findRecipe,
  findUserRecipe,
  onPrevMonth,
  onNextMonth,
  onSelectDay,
}) {
  const { theme } = useMCMTheme();
  const system = getMeasurementSystem();
  const today = useMemo(() => startOfDay(new Date()), []);
  const cells = useMemo(() => monthGrid(cursorDate, system), [cursorDate, system]);
  const letters = useMemo(() => weekDayLetters(system), [system]);

  // Pre-bucket all meals + cooks into entries keyed by dayKey so each
  // DayCell can render its emoji rows without re-walking the full
  // arrays. Entries shape:
  //   { kind: "meal"|"cook", emoji, status, time }
  // status drives the badge color: "cooked"|"planned"|"requested".
  const byDay = useMemo(() => {
    const out = new Map();
    const push = (key, entry) => {
      if (!out.has(key)) out.set(key, []);
      out.get(key).push(entry);
    };
    for (const m of meals) {
      const t = new Date(m.scheduled_for);
      const recipe = m.recipe_slug && findRecipe
        ? findRecipe(m.recipe_slug, findUserRecipe, m.recipe_user_id)
        : null;
      const emoji = recipe?.emoji || "🍽️";
      let status = "planned";
      if (m.status === "cooked") status = "cooked";
      else if (m.cook_id == null && !m.from_pantry_row_id) status = "requested";
      push(dayKey(t), { kind: "meal", emoji, status, time: t });
    }
    for (const c of pastCooks) {
      const t = new Date(c.cooked_at);
      push(dayKey(t), { kind: "cook", emoji: c.recipe_emoji || "🍽️", status: "cooked", time: t });
    }
    // Sort entries within a day by time so the morning's breakfast
    // appears above the evening's dinner.
    for (const arr of out.values()) {
      arr.sort((a, b) => a.time.getTime() - b.time.getTime());
    }
    return out;
  }, [meals, pastCooks, findRecipe, findUserRecipe]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 4px", gap: 8,
      }}>
        <button onClick={onPrevMonth} aria-label="Previous month"
          style={navBtnStyle(theme)}>
          ‹
        </button>
        <div style={{
          fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500,
          fontSize: 22, color: theme.color.skyInk || COLOR.ink,
          textAlign: "center", flex: 1,
        }}>
          {fmtMonthHeader(cursorDate)}
        </div>
        <button onClick={onNextMonth} aria-label="Next month"
          style={navBtnStyle(theme)}>
          ›
        </button>
      </div>

      {/* Day-of-week header row */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(7, 1fr)",
        padding: "0 4px",
      }}>
        {letters.map((L, i) => (
          <div key={i} style={{
            fontFamily: FONT.forma, fontSize: 10, fontWeight: 600,
            color: theme.color.skyInkMuted || COLOR.muted,
            letterSpacing: "0.16em",
            textAlign: "center",
            padding: "6px 0",
          }}>
            {L}
          </div>
        ))}
      </div>

      {/* Month grid */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`${cursorDate.getFullYear()}-${cursorDate.getMonth()}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18, ease: [0.32, 0.72, 0, 1] }}
          style={{
            background: theme.color.glassFillHeavy,
            border: `1px solid ${theme.color.glassBorder}`,
            borderRadius: 16,
            boxShadow: theme.shadow.glass,
            padding: 6,
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 4,
          }}
        >
          {cells.map(({ date, inMonth, key }) => {
            const entries = byDay.get(key) || [];
            const isToday = isSameDay(date, today);
            return (
              <DayCell
                key={key}
                date={date}
                inMonth={inMonth}
                isToday={isToday}
                entries={entries}
                theme={theme}
                onClick={() => onSelectDay?.(date)}
              />
            );
          })}
        </motion.div>
      </AnimatePresence>

      {/* Legend — quick visual reference for the status badges so
          users learn the vocabulary on first sight. */}
      <div style={{
        display: "flex", gap: 14, justifyContent: "center",
        padding: "8px 0 0", flexWrap: "wrap",
      }}>
        <LegendChip emoji="🍝" badge="✓" tone={COLOR.leaf} label="Cooked" />
        <LegendChip emoji="🥞" badge="•" tone={COLOR.gold} label="Planned" />
        <LegendChip emoji="🍳" badge="🙋" tone={COLOR.sky} label="Requested" />
      </div>
    </div>
  );
}

function DayCell({ date, inMonth, isToday, entries, theme, onClick }) {
  const visible = entries.slice(0, MAX_EMOJI_ROWS);
  const overflow = entries.length - visible.length;

  return (
    <button
      onClick={onClick}
      style={{
        height: CELL_HEIGHT,
        padding: "6px 4px 4px",
        display: "flex", flexDirection: "column", alignItems: "stretch",
        gap: 2,
        background: isToday ? COLOR.goldDeep : "transparent",
        border: isToday ? `2px solid ${COLOR.gold}` : "1px solid transparent",
        borderRadius: 10,
        cursor: "pointer",
        opacity: inMonth ? 1 : 0.32,
        transition: "background 160ms ease, border-color 160ms ease",
        textAlign: "left",
      }}
      // Hover affordance for MOUSE ONLY. iOS synthesizes mouseenter on
      // tap but mouseleave often never follows, so the "hover" border
      // stuck permanently on every tapped day — read as a black
      // hairline on the glass grid. pointerType gates the effect to
      // real pointers; leave stays unconditional so any stuck state
      // self-heals on the next interaction.
      onPointerEnter={e => {
        if (e.pointerType !== "mouse") return;
        if (!isToday) e.currentTarget.style.borderColor = "rgba(180,150,80,0.4)";
      }}
      onPointerLeave={e => {
        if (!isToday) e.currentTarget.style.borderColor = "transparent";
      }}
    >
      {/* Date number — top-left */}
      <div style={{
        fontFamily: FONT.forma,
        fontSize: 13, fontWeight: isToday ? 700 : 500,
        color: isToday ? COLOR.gold : (theme.color.skyInk || COLOR.ink),
        lineHeight: 1,
        paddingLeft: 2,
      }}>
        {date.getDate()}
      </div>

      {/* Emoji rows — one per meal, with a small status badge. */}
      <div style={{
        display: "flex", flexDirection: "column", gap: 1,
        flex: 1, minHeight: 0,
      }}>
        {visible.map((entry, i) => (
          <EntryRow key={i} entry={entry} />
        ))}
        {overflow > 0 && (
          <div style={{
            fontFamily: FONT.forma, fontSize: 9, fontWeight: 700,
            color: theme.color.skyInkMuted || COLOR.muted,
            paddingLeft: 2,
          }}>
            +{overflow} more
          </div>
        )}
      </div>
    </button>
  );
}

function EntryRow({ entry }) {
  const cooked = entry.status === "cooked";
  const requested = entry.status === "requested";
  const tone = cooked ? COLOR.leaf : requested ? COLOR.sky : COLOR.gold;
  const bg = cooked ? "rgba(47,143,131,0.14)"
    : requested ? "rgba(111,175,155,0.14)"
    : "rgba(212,166,55,0.14)";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 3,
      padding: "1px 3px",
      background: bg,
      borderRadius: 4,
      fontSize: 13, lineHeight: 1.1,
      overflow: "hidden",
    }}>
      <span style={{ flexShrink: 0 }}>{entry.emoji}</span>
      <span style={{
        fontFamily: FONT.forma, fontSize: 9, fontWeight: 700,
        color: tone, letterSpacing: "0.04em",
        flexShrink: 0,
      }}>
        {cooked ? "✓" : requested ? "?" : "•"}
      </span>
    </div>
  );
}

function LegendChip({ emoji, badge, tone, label }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      fontFamily: FONT.forma, fontSize: 10, fontWeight: 600,
      color: COLOR.muted, letterSpacing: "0.04em",
    }}>
      <span style={{ fontSize: 13 }}>{emoji}</span>
      <span style={{ color: tone, fontWeight: 700 }}>{badge}</span>
      <span>{label}</span>
    </div>
  );
}

function navBtnStyle(theme) {
  return {
    width: 36, height: 36, flexShrink: 0,
    borderRadius: 999,
    border: `1px solid ${theme.color.glassBorder}`,
    background: theme.color.glassFillHeavy,
    color: theme.color.skyInk || COLOR.ink,
    fontFamily: FONT.forma, fontSize: 20, fontWeight: 500,
    cursor: "pointer", padding: 0,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    boxShadow: theme.shadow.soft,
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
  };
}
